// ---------------------------------------------------------------------------
// InspectorPresencePanel — shows active field inspector tabs from localStorage
//
// Reads the 'firesquirrel-presence' key written by mobile-pwa portalChannel.ts.
// Updates live via storage events. Displayed on the system-web dashboard to
// give platform admins visibility into active field sessions.
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { Smartphone, Wifi, WifiOff } from 'lucide-react';

const PRESENCE_KEY = 'firesquirrel-presence';
const STALE_MS = 90_000;

interface PresenceEntry {
  tabId: string;
  portal: string;
  userId?: string;
  departmentId?: string;
  lastSeenAt: string;
}

function readPresence(): PresenceEntry[] {
  try {
    const raw = localStorage.getItem(PRESENCE_KEY);
    if (!raw) return [];
    const entries: PresenceEntry[] = JSON.parse(raw);
    const cutoff = Date.now() - STALE_MS;
    return entries.filter((e) => new Date(e.lastSeenAt).getTime() > cutoff);
  } catch {
    return [];
  }
}

function formatRelativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 10) return 'Just now';
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  return `${Math.floor(minutes / 60)}h ago`;
}

const PORTAL_COLORS: Record<string, { bg: string; text: string; dot: string }> = {
  mobile: { bg: 'bg-orange-50', text: 'text-orange-700', dot: 'bg-orange-500' },
  tenant: { bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
  system: { bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
};

export default function InspectorPresencePanel() {
  const [entries, setEntries] = useState<PresenceEntry[]>([]);

  useEffect(() => {
    setEntries(readPresence());

    const handleStorage = (e: StorageEvent) => {
      if (e.key === PRESENCE_KEY) setEntries(readPresence());
    };

    window.addEventListener('storage', handleStorage);
    const timer = setInterval(() => setEntries(readPresence()), 30_000);

    return () => {
      window.removeEventListener('storage', handleStorage);
      clearInterval(timer);
    };
  }, []);

  const mobileEntries = entries.filter((e) => e.portal === 'mobile');
  const otherEntries = entries.filter((e) => e.portal !== 'mobile');

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Smartphone className="h-5 w-5 text-orange-500" />
          Field App Presence
        </h2>
        <div className="flex items-center gap-1.5">
          {mobileEntries.length > 0 ? (
            <>
              <Wifi className="h-4 w-4 text-emerald-500" />
              <span className="text-xs font-medium text-emerald-600">
                {mobileEntries.length} active
              </span>
            </>
          ) : (
            <>
              <WifiOff className="h-4 w-4 text-gray-400" />
              <span className="text-xs font-medium text-gray-400">No field sessions</span>
            </>
          )}
        </div>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm text-gray-400 italic">
          No portal tabs detected in this browser. Field app presence requires the mobile-pwa to be open in the same browser.
        </p>
      ) : (
        <div className="space-y-2">
          {[...mobileEntries, ...otherEntries].map((entry) => {
            const colors = PORTAL_COLORS[entry.portal] ?? PORTAL_COLORS.system;
            return (
              <div
                key={entry.tabId}
                className={`flex items-center gap-3 px-3 py-2 rounded-lg ${colors.bg}`}
              >
                <div className={`w-2 h-2 rounded-full shrink-0 ${colors.dot}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-semibold capitalize ${colors.text}`}>
                      {entry.portal}
                    </span>
                    {entry.userId && (
                      <span className="text-xs text-gray-500 truncate">
                        …{entry.userId.slice(-6).toUpperCase()}
                      </span>
                    )}
                    {entry.departmentId && (
                      <span className="text-xs text-gray-400 truncate">
                        dept:{entry.departmentId.slice(-4)}
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-[10px] text-gray-400 shrink-0 font-mono">
                  {formatRelativeTime(entry.lastSeenAt)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <p className="mt-3 text-[10px] text-gray-400">
        Refreshes every 30s · TTL 90s · Same-browser localStorage only
      </p>
    </div>
  );
}
